import { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { getSenderPerspectiveLabel } from '@/lib/senderPerspective';
import { CinematicBurst } from './CinematicBurst';
import { RevealPhotos } from './RevealPhotos';
import { useRevealViewport, PreparingIndicator } from '../useRevealViewport';
import type { FullCanvasSceneProps } from './types';
import { STYLE_ALIGNMENT } from './types';
import { AdaptiveMessageBody } from '../cinematic/adaptive/AdaptiveMessageBody';
import { getReadingRevealCompleteMs } from './readingDwell';

import { DepthRevealLayer } from '../cinematic/DepthRevealLayer';
const EASE = [0.22, 0.61, 0.36, 1] as const;

const GOLD_COLORS = [
  '#fbbf24', '#f59e0b', '#d97706', '#fcd34d', '#fef08a',
  '#fde68a', '#fffbeb', '#facc15', '#eab308', '#ca8a04',
  '#b45309', '#f97316', '#fed7aa', '#fff7ed', '#fef3c7',
];

export function FullCanvasGoldenLight({ recipientName, relationship, message, photoUrls, publishedDate, onComplete, config, startAtMessage = false }: FullCanvasSceneProps) {
  const [phase, setPhase] = useState<'dark' | 'burst' | 'sweep' | 'text' | 'done'>(startAtMessage ? 'text' : 'dark');
  const [burstFired, setBurstFired] = useState(false);
  const words = message.split(/(\s+)/).filter(w => w.trim());
  const wordDelay = 0.12;
  const { isMobile, burstOriginY, timingMultiplier: tm, particleScale } = useRevealViewport();

  const t = (ms: number) => ms * tm;

  useEffect(() => {
    if (startAtMessage) {
      const tDone = setTimeout(() => { setPhase('done'); onComplete(); }, getReadingRevealCompleteMs({
        message,
        mode: config?.style.readingRhythm ?? 'word',
        pacing: config?.pacing ?? 1,
        startDelay: 1.2,
      }));
      return () => clearTimeout(tDone);
    }
    const t1 = setTimeout(() => { setPhase('burst'); setBurstFired(true); }, t(4000));
    const t2 = setTimeout(() => setPhase('sweep'), t(7000));
    const t3 = setTimeout(() => setPhase('text'), t(11500));
    const totalDuration = t(11500) / 1000 + 2.5 + words.length * wordDelay + 4.0;
    const t4 = setTimeout(() => { setPhase('done'); onComplete(); }, totalDuration * 1000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); };
  }, [config?.pacing, config?.style.readingRhythm, message, onComplete, startAtMessage, tm, words.length]);

  const isPostBurst = phase !== 'dark';
  const isSweepOrLater = phase === 'sweep' || phase === 'text' || phase === 'done';
  const isTextVisible = phase === 'text' || phase === 'done';

  const goldDust = useMemo(() =>
    Array.from({ length: Math.round(30 * particleScale) }, (_, i) => ({
      x: Math.random() * 100, y: Math.random() * 100,
      size: 1 + Math.random() * 3, duration: 3 + Math.random() * 4,
      delay: t(7000) / 1000 + Math.random() * 3, brightness: 0.3 + Math.random() * 0.7,
    })),
  [particleScale, tm]);

  return (
    <div className={`min-h-[100dvh] sm:min-h-screen relative ${isTextVisible ? 'overflow-y-auto overflow-x-hidden' : 'overflow-hidden'}`}>
      <motion.div className="absolute inset-0"
        style={{ background: 'linear-gradient(135deg, #0a0804 0%, #1a1408 30%, #0d0a04 100%)' }}
        animate={{ opacity: isSweepOrLater ? 0 : 1 }}
        transition={{ duration: 3.5, ease: EASE as any }}
      />

      <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #f9f3e3 0%, #fdf6e3 50%, #f5f0e8 100%)' }} />

      {/* Mobile preparing indicator */}
      {isMobile && <PreparingIndicator visible={phase === 'dark'} color="rgba(251,191,36,0.7)" />}

      {phase === 'dark' && (
        <>
          <motion.div className="absolute inset-0 pointer-events-none"
            style={{ background: `radial-gradient(circle at 50% ${burstOriginY}%, rgba(251,191,36,0.08) 0%, transparent 30%)` }}
            animate={{ opacity: [0, 1, 0.3, 1], scale: [0.8, 1.3, 0.9, 1.3] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
          />
          {[0, 1, 2, 3, 4, 5].map(i => (
            <motion.div key={`ember-${i}`} className="absolute rounded-full pointer-events-none"
              style={{
                width: isMobile ? 4 + i : 2 + i, height: isMobile ? 4 + i : 2 + i,
                left: `${35 + i * 6}%`, top: `${(burstOriginY - 5) + (i % 3) * 5}%`,
                background: GOLD_COLORS[i],
                boxShadow: `0 0 15px 6px ${GOLD_COLORS[i]}55`,
              }}
              animate={{ opacity: [0, 0.8, 0], y: [0, -15, 0] }}
              transition={{ duration: 1.8, delay: i * 0.3, repeat: Infinity, ease: 'easeInOut' }}
            />
          ))}
        </>
      )}

      <CinematicBurst active={burstFired} count={Math.round(80 * particleScale)} colors={GOLD_COLORS} shape="sparkle" originX={50} originY={burstOriginY} spread={isMobile ? 50 : 65} ambientCount={Math.round(25 * particleScale)} glowIntensity={3.5} />

      {isSweepOrLater && (
        <>
          <motion.div className="absolute top-0 bottom-0 w-64 z-20 pointer-events-none"
            style={{
              background: 'linear-gradient(to right, transparent, rgba(251,191,36,0.08) 15%, rgba(251,191,36,0.5) 45%, rgba(251,191,36,0.5) 55%, rgba(251,191,36,0.08) 85%, transparent)',
              boxShadow: '0 0 200px 80px rgba(251,191,36,0.15)',
            }}
            initial={{ left: '-25%' }} animate={{ left: '125%' }}
            transition={{ duration: 4.0, ease: [0.25, 0.46, 0.45, 0.94] }}
          />
          <motion.div className="absolute top-0 bottom-0 w-96 z-19 pointer-events-none"
            style={{ background: 'linear-gradient(to right, transparent, rgba(251,191,36,0.03) 20%, rgba(251,191,36,0.15) 50%, rgba(251,191,36,0.03) 80%, transparent)' }}
            initial={{ left: '-30%' }} animate={{ left: '130%' }}
            transition={{ duration: 4.5, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          />
          <motion.div className="absolute pointer-events-none z-20"
            style={{
              width: isMobile ? 200 : 400, height: isMobile ? 200 : 400, borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(251,191,36,0.35) 0%, rgba(251,191,36,0.06) 35%, transparent 65%)',
              top: '15%',
            }}
            initial={{ left: '-20%', opacity: 0 }} animate={{ left: '120%', opacity: [0, 1, 1, 0] }}
            transition={{ duration: 4.0, ease: [0.25, 0.46, 0.45, 0.94] }}
          />
        </>
      )}

      {isPostBurst && goldDust.map((d, i) => (
        <motion.div key={`dust-${i}`} className="absolute rounded-full pointer-events-none"
          style={{
            width: d.size, height: d.size, left: `${d.x}%`, top: `${d.y}%`,
            background: 'rgba(251,191,36,0.6)',
            boxShadow: `0 0 ${d.size * 4}px ${d.size * 2}px rgba(251,191,36,0.2)`,
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, d.brightness, d.brightness * 0.3, d.brightness], y: [0, -8, 0] }}
          transition={{ duration: d.duration, delay: d.delay, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}

      <motion.div className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at 50% 40%, rgba(251,191,36,0.06), transparent 60%)' }}
        initial={{ opacity: 0 }} animate={{ opacity: isSweepOrLater ? 1 : 0 }}
        transition={{ duration: 2 }}
      />

      <motion.div
        className="relative z-10 py-12 sm:py-16 md:py-20 px-4 sm:px-6 md:px-8"
        initial={{ opacity: 0 }} animate={{ opacity: isTextVisible ? 1 : 0 }}
        transition={{ duration: startAtMessage ? 1.8 : 1.2 }}
      >
        <div className="max-w-2xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={isTextVisible ? { opacity: 1, y: 0 } : {}} transition={{ duration: 1.0, ease: EASE as any }}>
            <p className="text-xs uppercase tracking-[0.3em] text-amber-700/70 mb-3">A message of light</p>
            <h1 className="font-serif text-3xl md:text-5xl text-amber-950 mb-4 tracking-wide">For {recipientName}</h1>
            <div className="flex items-center justify-center gap-4 mb-8">
              <div className="w-16 h-px bg-amber-600/50" />
              <motion.div className="w-2 h-2 rounded-full bg-amber-600/60"
                animate={{ boxShadow: ['0 0 0 0 rgba(251,191,36,0)', '0 0 15px 5px rgba(251,191,36,0.3)', '0 0 0 0 rgba(251,191,36,0)'] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              <div className="w-16 h-px bg-amber-600/50" />
            </div>
          </motion.div>

          <RevealPhotos photoUrls={photoUrls} visible={isTextVisible} delay={0.8} borderColor="rgba(251,191,36,0.5)" shadowColor="rgba(180,130,20,0.2)" />

          <div className="mb-12">
            <AdaptiveMessageBody
              message={message}
              mode={config?.style.readingRhythm ?? 'word'}
              pacing={config?.pacing ?? 1}
              visible={isTextVisible}
              startDelay={1.2}
              alignment={config ? STYLE_ALIGNMENT[config.styleId] : 'centered'}
              textColorClass="text-amber-950"
              emphasisGlow="rgba(251,191,36,0.55)"
              typography={config?.typography ?? "serif-classic"}
            />
          </div>

          <motion.div initial={{ opacity: 0 }} animate={isTextVisible ? { opacity: 1 } : {}}
            transition={{ delay: 2 + words.length * wordDelay, duration: 1.0 }}
          >
            <p className="text-lg font-serif italic text-amber-800">With warmth,</p>
            <p className="text-xl font-serif text-amber-900 mt-1">— From your {getSenderPerspectiveLabel(relationship)}</p>
            <div className="mt-10 pt-6 border-t border-amber-300/50">
              <p className="text-xs text-amber-600/70 tracking-wider">Say It NOW • While They're Still Here</p>
              {publishedDate && <p className="text-xs text-amber-500/50 mt-1">Shared on {publishedDate}</p>}
            </div>
          </motion.div>
        </div>
      </motion.div>
      <DepthRevealLayer active={isTextVisible} tint="rgba(251,191,36,0.6)" glow="rgba(254,240,138,0.55)" />
    </div>
  );
}
